import { z } from 'zod';
import type { PlaycastMessage, PlaycastGameSaveMessages, PlaycastGameSaveStorageMessages } from '../types';
import { gameSaveCampaignConfigurationSchema } from '../types/api/gameSaveCampaignConfiguration';

export const gameSaveCampaignSchema = z.object({
  campaignId: z.string().trim().min(1).max(256),
  launchPackage: z.discriminatedUnion('launcher', [
    z.object({ launcher: z.literal('steam'), launchId: z.string().regex(/^[1-9][0-9]{0,9}$/) }).strict(),
    z.object({
      launcher: z.literal('android'),
      package: z.string().max(256).regex(/^[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)+$/),
    }).strict(),
  ]),
}).strict();

const requestIdSchema = z.string().uuid();
export const gameSaveErrorSchema = z.object({
  code: z.enum(['not_found', 'conflict', 'invalid_target', 'game_running', 'permission_denied',
    'transfer_failed', 'checksum_mismatch', 'deadline_exceeded', 'internal']),
  retryable: z.boolean(), message: z.string().min(1).max(2048),
}).strict();
export const gameSaveOperationSchema = z.object({
  requestId: requestIdSchema, campaign: gameSaveCampaignSchema,
  requireStopped: z.boolean(), slotId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/),
  configuration: gameSaveCampaignConfigurationSchema.optional(),
}).strict();
const checkSchema = z.object({ requestId: requestIdSchema, campaign: gameSaveCampaignSchema,
  configuration: gameSaveCampaignConfigurationSchema.optional() }).strict();
const failureSchema = z.object({ status: z.literal('failed'), error: gameSaveErrorSchema }).strict();
const saveReplySchema = z.object({
  requestId: requestIdSchema,
  result: z.union([z.object({ status: z.literal('saved') }).strict(), failureSchema]),
}).strict();
const loadReplySchema = z.object({
  requestId: requestIdSchema,
  result: z.union([z.object({ status: z.literal('loaded') }).strict(), failureSchema]),
}).strict();
const checkReplySchema = z.object({
  requestId: requestIdSchema, configured: z.boolean(), error: gameSaveErrorSchema.optional(),
}).strict();

export function validateGameSaveMessage(message: unknown):
  PlaycastMessage<PlaycastGameSaveMessages> | undefined {
  if (!message || typeof message !== 'object') return undefined;
  const envelope = message as PlaycastMessage<PlaycastGameSaveMessages>;
  const header = envelope.header;
  if (header?.target !== 'process' || typeof header.isReply !== 'boolean' ||
    header.source !== (header.isReply ? 'playjector' : 'host')) return undefined;
  let schema: z.ZodTypeAny;
  switch (header.action) {
    case 'saveGame': schema = header.isReply ? saveReplySchema : gameSaveOperationSchema; break;
    case 'loadGame': schema = header.isReply ? loadReplySchema : gameSaveOperationSchema; break;
    case 'checkGameSaveConfiguration': schema = header.isReply ? checkReplySchema : checkSchema; break;
    default: return undefined;
  }
  return schema.safeParse(envelope.body?.message).success ? envelope : undefined;
}

const uuid = z.string().uuid();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const slot = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/);
const bytes = z.number().int().positive().max(4294967295);
const revision = z.string().min(1).max(256).nullable();
const snapshotId = z.string().min(1).max(256);
const timestamp = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const httpsUrl = z.string().max(8192).url().refine((value) => {
  const url = new URL(value);
  return url.protocol === 'https:' && !url.username && !url.password && (!url.port || url.port === '443');
});
// Signed values must survive parsing byte-for-byte; the ordinary campaign schema trims its ID.
const signedCampaignSchema = gameSaveCampaignSchema.extend({
  campaignId: z.string().min(1).max(256).refine((value) => value.trim().length > 0),
});
const identity = {
  requestId: uuid, campaign: signedCampaignSchema, targetKey: hash, slotId: slot,
  machineId: z.string().min(1).max(256), issuedAt: timestamp,
  signature: z.string().min(1).max(4096).regex(/^[A-Za-z0-9+/]+={0,2}$/),
};

export const gameSaveStorageRequestSchema = z.discriminatedUnion('action', [
  z.object({ ...identity, action: z.literal('upload'), sha256: hash, bytes }).strict(),
  z.object({ ...identity, action: z.literal('commit'), sha256: hash, bytes,
    uploadId: uuid, expectedRevision: revision }).strict(),
  z.object({ ...identity, action: z.literal('download') }).strict(),
]);
const uploadHeaders = z.object({
  'content-type': z.literal('application/zip'),
  'x-amz-checksum-sha256': z.string().regex(/^[A-Za-z0-9+/]{43}=$/),
  'if-none-match': z.literal('*'),
}).strict();
export const gameSaveStorageReplySchema = z.union([
  z.object({ requestId: uuid, action: z.literal('upload'), status: z.literal('ok'),
    uploadId: uuid, url: httpsUrl, headers: uploadHeaders, expectedRevision: revision,
    expiresAt: timestamp }).strict(),
  z.object({ requestId: uuid, action: z.literal('commit'), status: z.literal('ok'), snapshotId }).strict(),
  z.object({ requestId: uuid, action: z.literal('download'), status: z.literal('ok'),
    url: httpsUrl, sha256: hash, bytes, snapshotId, manifestSlotId: slot, expiresAt: timestamp }).strict(),
  z.object({ requestId: uuid, action: z.enum(['upload', 'commit', 'download']),
    status: z.literal('failed'), error: gameSaveErrorSchema }).strict(),
]);

export function validateGameSaveStorageMessage(message: unknown):
  PlaycastMessage<PlaycastGameSaveStorageMessages> | undefined {
  if (!message || typeof message !== 'object') return undefined;
  const envelope = message as PlaycastMessage<PlaycastGameSaveStorageMessages>;
  const header = envelope.header;
  if (header?.target !== 'process' || header.action !== 'gameSaveStorage' ||
    typeof header.isReply !== 'boolean' || header.source !== (header.isReply ? 'host' : 'playjector')) return undefined;
  const schema = header.isReply ? gameSaveStorageReplySchema : gameSaveStorageRequestSchema;
  return schema.safeParse(envelope.body?.message).success ? envelope : undefined;
}
